'use client';

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from 'react';
import type { CompositionSegment } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { COMPOSITION_MIN_SEGMENT_WIDTH_PX, COMPOSITION_BAR_HEIGHT_CLASS } from '@/domains/pages/ai-spend/constants';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { pieChartAccentStyle, POWER_BAR_TRACK_CLASS } from '@/domains/pages/shared/power-bar-shared';
import { formatPercentage, formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useCallback, useRef, useState } from 'react';
type SpendCompositionBarProps = {
  segments: CompositionSegment[];
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  collapseBelowShare?: number;
  className?: string;
};
export function SpendCompositionBar(t0) {
  const $ = _c(49);
  const {
    segments,
    hoveredKey,
    onHoverChange,
    className
  } = t0;
  const trackRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t1);
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== className || $[2] !== hoveredKey || $[3] !== onHoverChange || $[4] !== segments || $[5] !== tooltipPos) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const hoveredSegment = segments.find(segment => segment.key === hoveredKey) ?? null;
      let t10;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = (clientX, clientY) => {
          setTooltipPos({
            x: clientX,
            y: clientY
          });
        };
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      const updateTooltipPosition = t10;
      let t11;
      if ($[16] !== onHoverChange) {
        t11 = (segment_0, event) => {
          onHoverChange(segment_0.key);
          updateTooltipPosition(event.clientX, event.clientY);
        };
        $[16] = onHoverChange;
        $[17] = t11;
      } else {
        t11 = $[17];
      }
      const handleSegmentEnter = t11;
      let t12;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = event_0 => {
          updateTooltipPosition(event_0.clientX, event_0.clientY);
        };
        $[18] = t12;
      } else {
        t12 = $[18];
      }
      const handleSegmentMove = t12;
      let t13;
      if ($[19] !== onHoverChange) {
        t13 = () => {
          onHoverChange(null);
        };
        $[19] = onHoverChange;
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      const handleTrackLeave = t13;
      if (segments.length === 0) {
        let t14;
        if ($[21] !== className) {
          t14 = cn(POWER_BAR_TRACK_CLASS, COMPOSITION_BAR_HEIGHT_CLASS, className);
          $[21] = className;
          $[22] = t14;
        } else {
          t14 = $[22];
        }
        let t15;
        if ($[23] !== t14) {
          t15 = <div className={t14} data-testid="spend-composition-bar" />;
          $[23] = t14;
          $[24] = t15;
        } else {
          t15 = $[24];
        }
        t9 = t15;
        break bb0;
      }
      if ($[25] !== className) {
        t6 = cn("relative", className);
        $[25] = className;
        $[26] = t6;
      } else {
        t6 = $[26];
      }
      t7 = "spend-composition-bar";
      let t14;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = cn("flex overflow-hidden", POWER_BAR_TRACK_CLASS, COMPOSITION_BAR_HEIGHT_CLASS);
        $[27] = t14;
      } else {
        t14 = $[27];
      }
      let t15;
      if ($[28] !== handleSegmentEnter || $[29] !== hoveredKey || $[30] !== segments) {
        let t16;
        if ($[32] !== handleSegmentEnter || $[33] !== hoveredKey) {
          t16 = segment_1 => {
            const isHovered = hoveredKey === segment_1.key;
            const isDimmed = hoveredKey != null && !isHovered;
            return <div key={segment_1.key} className={cn("h-full cursor-pointer transition-opacity", isDimmed && "opacity-35", isHovered && "opacity-100")} style={{
              width: `${Math.max(segment_1.share * 100, 0)}%`,
              minWidth: `${COMPOSITION_MIN_SEGMENT_WIDTH_PX}px`,
              ...pieChartAccentStyle(segment_1.color)
            }} onMouseEnter={event_1 => handleSegmentEnter(segment_1, event_1)} onMouseMove={handleSegmentMove} />;
          };
          $[32] = handleSegmentEnter;
          $[33] = hoveredKey;
          $[34] = t16;
        } else {
          t16 = $[34];
        }
        t15 = segments.map(t16);
        $[28] = handleSegmentEnter;
        $[29] = hoveredKey;
        $[30] = segments;
        $[31] = t15;
      } else {
        t15 = $[31];
      }
      if ($[35] !== handleTrackLeave || $[36] !== t15) {
        t8 = <div ref={trackRef} className={t14} onMouseLeave={handleTrackLeave}>{t15}</div>;
        $[35] = handleTrackLeave;
        $[36] = t15;
        $[37] = t8;
      } else {
        t8 = $[37];
      }
      T0 = FloatingTooltip;
      t2 = hoveredSegment != null;
      t3 = tooltipPos.x;
      t4 = tooltipPos.y;
      t5 = hoveredSegment ? <ChartTooltipPanel title={hoveredSegment.label} rows={[{
        label: "Spend",
        value: formatSpend(hoveredSegment.value)
      }, {
        label: "Share",
        value: formatPercentage(hoveredSegment.share, {
          inputScale: "decimal",
          decimals: 1
        })
      }]} /> : null;
    }
    $[1] = className;
    $[2] = hoveredKey;
    $[3] = onHoverChange;
    $[4] = segments;
    $[5] = tooltipPos;
    $[6] = T0;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    T0 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[38] !== T0 || $[39] !== t2 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5) {
    t10 = <T0 open={t2} x={t3} y={t4}>{t5}</T0>;
    $[38] = T0;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t6 || $[46] !== t7 || $[47] !== t8) {
    t11 = <div className={t6} data-testid={t7}>{t8}{t10}</div>;
    $[44] = t10;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
}